import React, { FormEvent, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import I18n from '@/modules/i18n';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import { useClassrooms } from './NotifyStudentsModal.utils';
import NotifyStudentsForm from './NotifyStudentsForm';
import SelectedStudentsNotice from './SelectedStudentsNotice';

interface NotifyStudentsModalProps {
  alreadyJoinedStudentIds?: number[];
  close: () => void;
  isError?: boolean;
  isOpen: boolean;
  isSubmitting?: boolean;
  onSubmit: (studentIds: number[]) => void;
  programId: number;
  stafferId: number;
  submitText?: string;
  submittingText?: string;
}

const NotifyStudentsModal = ({
  alreadyJoinedStudentIds = [],
  close,
  isError,
  isOpen,
  isSubmitting,
  onSubmit,
  programId,
  stafferId,
  submitText = I18n.t('start_teacher_paced_mode'),
  submittingText = I18n.t('starting_session'),
}: NotifyStudentsModalProps) => {
  const { data } = useClassrooms({ programId, stafferId });
  const form = useForm({ defaultValues: {} });
  const allValues = form.watch();
  const isValid = Object.values(allValues).flat().length > 0;

  useEffect(() => {
    const values = data.reduce((acc, cur) => { acc[cur.id.toString()] = []; return acc; }, {});
    form.reset(values);
  }, [data]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const allIds = Object.values(form.getValues()).flat().map(Number);
    onSubmit(allIds);
  };

  return (
    <FormProvider {...form}>
      <SubmitModal
        bodyClassName="tw-flex-1 tw-min-h-0 tw-box-border !tw-max-h-none"
        className={cn(
          'tw-w-1/3 tw-max-w-[420px] tw-aspect-[0.7] tw-flex tw-flex-col',
        )}
        close={close}
        footerWrapperClassName="tw-bg-white tw-mt-auto"
        headerText={I18n.t('notify_students_modal_header')}
        isLoading={isSubmitting}
        isOpen={isOpen}
        isValid={isValid}
        submitText={submitText}
        submittingText={submittingText}
        onSubmit={handleSubmit}
      >
        <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col">
          <Text size="sm">
            {I18n.t('notify_students_modal_body')}
          </Text>
          <NotifyStudentsForm alreadyJoinedStudentIds={alreadyJoinedStudentIds} classrooms={data} />
          <SelectedStudentsNotice />
          {isError && (
            <div className="tw-text-red tw-mt-2" role="alert">
              <Text size="sm">{I18n.t('notify_students_error')}</Text>
            </div>
          )}
        </div>
      </SubmitModal>
    </FormProvider>
  );
};

export default NotifyStudentsModal;
